import {RiverBreakout, Text, Link, Stack, Statistic, Card, Box, Image} from '@primer/react-brand/esm'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {getImageSources} from '@github-ui/swp-core/lib/utils/images'
import {getAssetImageStyle, renderWithoutParagraphs, renderPoints} from '../../utils'
import {AssetTypeEnum} from '../../types'
import type {RiverBreakout as RiverBreakoutProps} from '../../types'
import {AssetVideo} from '../Video'
import {t} from '@github-ui/swp-core/lib/utils/localization'
import styles from './FlexSuiteRiverBreakout.module.css'

export type FlexSuiteRiverBreakoutProps = RiverBreakoutProps['fields']

export const FlexSuiteRiverBreakout = ({
  heading,
  includeCta,
  label,
  href,
  alt,
  supportingContent,
  pointOne,
  pointTwo,
  pointThree,
  statistics,
  customerStories,
  assetType,
  optionImage,
  optionVideo,
  optionPoster,
  autoplay,
  videoSource,
}: FlexSuiteRiverBreakoutProps) => {
  const imageStyle = getAssetImageStyle(optionImage)
  let trailingContent
  if (supportingContent === 'Points') {
    trailingContent = () => (
      <Stack gap="normal">
        {pointOne && renderPoints(pointOne)}
        {pointTwo && renderPoints(pointTwo)}
        {pointThree && renderPoints(pointThree)}
      </Stack>
    )
  }

  if (supportingContent === 'Statistics') {
    trailingContent = () => {
      const stats = statistics?.map(stat => {
        const id = stat.sys.id
        const {heading: statHeading, description} = stat.fields
        return (
          <Statistic key={id}>
            <Statistic.Heading weight="normal" size="800" style={{color: 'var(--brand-color-text-emphasized)'}}>
              {renderWithoutParagraphs(statHeading)}
            </Statistic.Heading>
            <Statistic.Description>{renderWithoutParagraphs(description)}</Statistic.Description>
          </Statistic>
        )
      })

      return (
        <Stack gap="normal" padding="none">
          <Box padding="normal">{stats}</Box>
        </Stack>
      )
    }
  }
  if (supportingContent === 'Customer Stories' && customerStories?.length) {
    /*
     * This is a temporary implementation for customer stories.
     * It will be replaced when the proper component is ready in the Brand Refactor.
     */
    const stories = customerStories.map(story => {
      const id = story.sys.id
      const {logo, lead, url, title} = story.fields
      return (
        <Box key={id} padding="condensed">
          <Card href={url} variant="minimal" fullWidth disableAnimation ctaText={t('Learn more')}>
            <Card.Image src={logo.fields.file.url} alt={logo.fields.description || title} height={40} />
            <Card.Heading>{title}</Card.Heading>
            <Card.Description>{lead}</Card.Description>
          </Card>
        </Box>
      )
    })

    trailingContent = () => <Stack gap="normal">{stories}</Stack>
  }
  return (
    <RiverBreakout variant="gridline">
      <RiverBreakout.A11yHeading>{documentToPlainTextString(heading)}</RiverBreakout.A11yHeading>
      <RiverBreakout.Visual className={styles.visual}>
        {assetType === AssetTypeEnum.enum.Image && optionImage && (
          <Image
            as="picture"
            aspectRatio={imageStyle ? 'custom' : undefined}
            src={`${optionImage.fields.file.url}?fm=webp`}
            sources={getImageSources(optionImage.fields.file.url, {maxWidth: 1440})}
            style={imageStyle}
            alt={optionImage.fields.description ?? ''}
            loading="lazy"
          />
        )}
        <AssetVideo
          assetType={assetType}
          optionVideo={optionVideo}
          optionPoster={optionPoster}
          autoplay={autoplay}
          videoSource={videoSource}
        />
      </RiverBreakout.Visual>
      <RiverBreakout.Content {...(trailingContent && {trailingComponent: trailingContent})} trailingComponentDivider>
        {[
          <Text key={heading.nodeType}>{renderWithoutParagraphs(heading)}</Text>,
          ...(includeCta && href && label
            ? [
                <Link key={href} href={href} {...(alt ? {'aria-label': alt} : {})}>
                  {label}
                </Link>,
              ]
            : []),
        ]}
      </RiverBreakout.Content>
    </RiverBreakout>
  )
}
